/* eslint-disable max-len */
import { NavigateFunction } from 'react-router-dom';
import { getSubRouteAbsolutePath, RouteGroup, TimeSeriesType } from '@moderepo/mode-app-components';
import { UsageMode } from '@moderepo/mode-ui-state';
import { DEFAULT_ROUTE_PATHS } from './AppRoutes';
import { DEFAULT_SUB_ROUTE_NAME } from '../../utils/ritzExtensionUtils';
import { EntitiesListingViewType } from '../../components';


/**
 * Given a Route URL e.g. '/projects/:projectId/devices/:deviceId/data', replace all the route params e.g. :projectId, :deviceId, etc...
 * with the values from the params. IMPORTANT NOTE: the keys in the params MUST match the route params. In this example, the params object
 * must contain these name/value pairs {projectId:1234, deviceId:6789}
 * @param url
 * @param params
 */
export const fillInRouteParams = (url: string, params: {[name: string]: string | number | {} | undefined | null}): string => {
    return Object.keys(params).reduce((result: string, key: string): string => {
        const value = params[key];
        if (value !== undefined && value !== null) {
            return result.replace(`:${key}`, value.toString());
        }
        return result;
    }, url);
};


/**
 * Convert an Object {x:1, y:"abc"} to query param string e.g. "?x=1&y=abc"
 */
const queryParamObjectToString = (queryParams: {}): string => {
    return `?${Object.entries(queryParams).filter(([, value]) => {
        // filter out search params that are undefined
        return value !== undefined && value !== null;
    }).map(([key, value]) => {
        if (value !== undefined) {
            return `${key}=${value}`;
        }
        return '';
    }).join('&')}`;
};


/**
 * Every time we navigate to a page, we have to use router's history.push or history.replace and we need to specify the route URL.
 * The problem with this is that we need to know the URL scheme for each route so we have to keep going back to the AppRoutes.tsx
 * to see how to create this URL.
 *
 * So, we have created this class to help with navigation. This allow us to navigate to any route without having to know how to construct
 * the URL. All we need to do is provide the necessary parameters for the function to create the route URL. This also make it easier to change
 * the URL scheme later on if we need to. All we have to do is make the change here instead of changing everywhere in the code.
 *
 * Each page will have 2 functions. One function will return the page's url, e.g. getLoginPageUrl, which can be used when we only need
 * the URL, not navigate to the page automatically. Another function will automatically navigate the user to another page e.g.
 * gotoLogin.
 *
 * @param history - OPTIONAL. If history is not provided, the user can only use one of the get page url functions but not the goto functions.
 */
export class RouteHelper {
    private static instance: RouteHelper;

    public static getInstance (navigate?: NavigateFunction) {
        // create a new Route Helper instance if we have not create one yet or if we have a 'history' object now but didn't have one before
        if (!RouteHelper.instance || (navigate && !RouteHelper.instance.navigate)) {
            RouteHelper.instance = new RouteHelper(navigate);
        }
        return RouteHelper.instance;
    }


    private navigate: NavigateFunction | undefined;

    private constructor (navigate?: NavigateFunction) {
        this.navigate = navigate;
    }

    /**
     * NOTE: if URL is RELATIVE, it will be relative to the route's root path which might not be where we expected. For example, if url is
     * 'logs' and we are at this URL https://cs.tinkermode.com/projects/1234/admin/settings, we expect it will take the user to
     * https://cs.tinkermode.com/projects/1234/admin/logs but it not always true. The the route's root path is
     * https://cs.tinkermode.com/projects/1234/, it will take the user to https://cs.tinkermode.com/projects/1234/logs.
     * So when calling this function, use absolute path if possible.
     */
    public navigateTo (url: string, replace: boolean = false, delay: number = 0) {
        if (delay > 0) {
            // If there is a delay required, wait 'delay' ms and then call route again with the same params but without delay next time.
            window.setTimeout(() => {
                this.navigateTo(url, replace, 0);
            }, delay);
            return;
        }

        if (this.navigate) {
            this.navigate(url, { replace });
        }
    }



    /**
     * Get the Login page URL
     */
    public getLoginPageUrl (): string {
        return DEFAULT_ROUTE_PATHS.LOGIN;
    }

    /**
     * Take user to the login page
     */
    public gotoLoginPage (replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getLoginPageUrl(), replace, delay);
    }

    /**
     * Get the Forgot Password page URL
     */
    public getForgotPasswordPageUrl (): string {
        return DEFAULT_ROUTE_PATHS.FORGOT_PASSWORD;
    }

    /**
     * Take user to the Forgot Password page
     */
    public gotoForgotPasswordPage (orgId?: string, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getForgotPasswordPageUrl(), replace, delay);
    }

    /**
     * Get the Home page URL
     */
    public getRitzHomePageUrl (): string {
        return DEFAULT_ROUTE_PATHS.HOME as string;
    }

    /**
     * Go to the first page where the user can select a project from a list of projects
     */
    public gotoRitzHomePage (replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getRitzHomePageUrl(), replace, delay);
    }

    /**
     * Get the Project Home page URL
     */
    public getProjectHomePageUrl (projectId: number): string {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId as string, { projectId });
    }

    /**
     * Go to the project's home page where we show the dashboard and the project info/features
     */
    public gotoProjectHomePage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getProjectHomePageUrl(projectId), replace, delay);
    }


    /**
     * Get the Project devices page URL
     */
    public getProjectDevicesPageUrl (projectId: number): string {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DEVICES as string, { projectId });
    }

    /**
     * Go to the Project Devices page
     */
    public gotoProjectDevicesPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getProjectDevicesPageUrl(projectId), replace, delay);
    }

    /**
     * Get the My Account page URL
     */
    public getMyAccountPageUrl (projectId?: number): string {
        if (projectId) {
            return `${this.getProjectHomePageUrl(projectId)}${DEFAULT_ROUTE_PATHS.MY_ACCOUNT}`;
        }
        return DEFAULT_ROUTE_PATHS.MY_ACCOUNT as string;
    }

    /**
     * Go to My Account page where the user can view/edit his own account
     */
    public gotoMyAccountPage (projectId?: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getMyAccountPageUrl(projectId), replace, delay);
    }

    /**
     * Get the Project Settings page URL
     */
    public getProjectSettingsPageUrl (projectId: number): string {
        return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ADMIN as RouteGroup, DEFAULT_SUB_ROUTE_NAME.DEFAULT), { projectId })}`;
    }

    /**
     * Go to the project's settings page
     */
    public gotoProjectSettingsPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getProjectSettingsPageUrl(projectId), replace, delay);
    }

    /**
     * Get the Agents list page URL
     */
    public getAgentsPageUrl (projectId: number) {
        return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ADMIN as RouteGroup, DEFAULT_SUB_ROUTE_NAME.AGENTS), { projectId })}`;
    }

    /**
     * GO to the page where we show all the agents belonged to the selected project
     */
    public gotoAgentsPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getAgentsPageUrl(projectId), replace, delay);
    }

    /**
     * Get the Admin page URL
     */
    public getAdminPageUrl (params: {
        readonly projectId: number;
    }) {
        return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ADMIN as RouteGroup).rootPath, params);
    }

    /**
     * Go to the Admin page
     */
    public gotoAdminPage (params: {
        readonly projectId: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getAdminPageUrl(params), replace, delay);
    }

    /**
     * Get the user details page URL
     */
    public getUserPageUrl (params: {
        readonly projectId: number;
        readonly userId: number
    }) {
        return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId as RouteGroup).rootPath, params);
    }

    /**
     * Go to the page where we show user details
     */
    public gotoUserPage (params: {
        readonly projectId: number;
        readonly userId: number
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getUserPageUrl(params), replace, delay);
    }

    /**
     * Get the user details page URL
     */
    public getUserLogsPageUrl (params: {
        readonly projectId: number;
        readonly userId: number;
        readonly logType?: string;
    }) {
        const query = params.logType ? `?logType=${params.logType}` : '';
        return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId as RouteGroup, DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
    }

    /**
     * Go to the page where we show user details
     */
    public gotoUserLogsPage (params: {
        readonly projectId: number;
        readonly userId: number;
        readonly logType?: string;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getUserLogsPageUrl(params), replace, delay);
    }

    /**
     * Get the user's specific home page URL
     */
    public getHomeDetailsPageUrl (params: {
        readonly projectId: number;
        readonly homeId: number;
        readonly userId?: number | undefined
    }) {
        if (params.userId) {
            return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup).rootPath, params);
        }
        return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup).rootPath, params);
    }

    /**
     * Go to the page that show one of the user's home details
     */
    public gotoHomeDetailsPage (params: {
        readonly projectId: number;
        readonly homeId: number;
        readonly userId?: number | undefined
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getHomeDetailsPageUrl(params), replace, delay);
    }

    /**
     * Get the home's logs page url
     */
    public getHomeLogsPageUrl (params: {
        readonly projectId: number;
        readonly userId?: number | undefined;
        readonly homeId: number;
        readonly logType?: string | undefined;
    }) {
        const query = params.logType ? `?logType=${params.logType}` : '';
        if (params.userId) {
            return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup, DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
        }
        return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup, DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
    }

    /**
     * Go to the home's log page
     */
    public gotoHomeLogsPage (params: {
        readonly projectId: number;
        readonly homeId: number;
        readonly logType?: string;
        readonly userId?: number | undefined;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getHomeLogsPageUrl(params), replace, delay);
    }

    /**
     * Get the device's details page
     */
    public getDevicePageUrl (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }) {
        if (params.userId && params.homeId) {
            return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup).rootPath, params);
        }
        if (params.homeId) {
            return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup).rootPath, params);
        }
        return fillInRouteParams((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup).rootPath, params);
    }

    /**
     * Go to the page that show a device details
     */
    public gotoDevicePage (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getDevicePageUrl(params), replace, delay);
    }

    /**
     * Get the device's logs page url
     */
    public getDeviceLogsPageUrl (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly logType?: string;
        readonly userId?: number;
        readonly homeId?: number
    }) {
        const query = params.logType ? `?logType=${params.logType}` : '';
        if (params.userId && params.homeId) {
            return `${fillInRouteParams(getSubRouteAbsolutePath((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup), DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
        }
        if (params.homeId) {
            return `${fillInRouteParams(getSubRouteAbsolutePath((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup), DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
        }
        return `${fillInRouteParams(getSubRouteAbsolutePath((DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId as RouteGroup), DEFAULT_SUB_ROUTE_NAME.LOGS), params)}${query}`;
    }

    /**
     * Go to the device's log page
     */
    public gotoDeviceLogsPage (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly logType?: string;
        readonly userId?: number;
        readonly homeId?: number
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getDeviceLogsPageUrl(params), replace, delay);
    }

    /**
     * Get the Agent Action log page URL
     */
    public getAuditLogsPageUrl (projectId: number) {
        return `${fillInRouteParams(getSubRouteAbsolutePath(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ADMIN as RouteGroup, DEFAULT_SUB_ROUTE_NAME.AUDIT_LOGS), { projectId })}`;
    }

    /**
     * Go to the Agent Action log page where we show all the action done by the agents
     */
    public gotoAuditLogsPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getAuditLogsPageUrl(projectId), replace, delay);
    }

    /**
     * Get the homes details page URL
     */
    public getHomesPageUrl (projectId: number) {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES as string, { projectId });
    }

    /**
     * Go to the page that list all the users for the selected project.
     */
    public gotoHomesPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getHomesPageUrl(projectId), replace, delay);
    }

    /**
     * Get the users list page URL
     */
    public getUsersPageUrl (projectId: number) {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS as string, { projectId });
    }

    /**
     * Go to the page that list all the users for the selected project.
     */
    public gotoUsersPage (projectId: number, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getUsersPageUrl(projectId), replace, delay);
    }


    /**
     * Get the Download Data page URL for Project related route
     */
    public getDownloadExportedDataPageUrl (projectId: number): string {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_DOWNLOADS as string, { projectId });
    }

    /**
     * Take user to the Download data page for project related route
     */
    public gotoDownloadExportedDataPage (projectId: number, replace: boolean = false) {
        this.navigateTo(this.getDownloadExportedDataPageUrl(projectId), replace);
    }


    /**
     * Get the Not Authorized page URL
     */
    public getNotAuthorizedPageUrl (): string {
        return DEFAULT_ROUTE_PATHS.NOT_AUTHORIZED as string;
    }

    /**
     * Take user to the Not Authorized page
     */
    public gotoNotAuthorizedPage (replace: boolean = false) {
        this.navigateTo(this.getNotAuthorizedPageUrl(), replace);
    }


    /**
     * Get the Not Authorized page URL for Project related route
     */
    public getProjectNotAuthorizedPageUrl (projectId: number): string {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_NOT_AUTHORIZED as string, { projectId });
    }

    /**
     * Take user to the Not Authorized page for project related route
     */
    public gotoProjectNotAuthorizedPage (projectId: number, replace: boolean = false) {
        this.navigateTo(this.getProjectNotAuthorizedPageUrl(projectId), replace);
    }

    /**
     * Get the view entities listing page URL
     */
    public getEntitiesPageUrl (projectId: number, queryParams?: {
        readonly viewType?: EntitiesListingViewType | undefined;
        readonly entityClasses?: readonly string[] | undefined;
    }, usageMode: UsageMode = UsageMode.VIEW_DATA) {
        // Build the query search params from the querySearchParams object e.g. "?a=123&b=456&c=abc"
        const query = queryParams ? queryParamObjectToString(queryParams) : '';
        if (usageMode === UsageMode.VIEW_DATA) {
            return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_VIEW_ENTITIES, { projectId })}${query}`;
        }
        return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_MANAGE_ENTITIES, { projectId })}${query}`;
    }

    /**
     * Go to the page that list all the entities for the selected project.
     */
    public gotoEntitiesPage (projectId: number, queryParams?: {
        readonly viewType?: EntitiesListingViewType | undefined;
        readonly entityClasses?: readonly string[] | undefined;
    }, usageMode: UsageMode = UsageMode.VIEW_DATA, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getEntitiesPageUrl(projectId, queryParams, usageMode), replace, delay);
    }


    /**
     * Get the entity details page URL
     */
    public getEntityDetailsPageUrl (projectId: number, entityId: string, viewIndex?: number | undefined) {
        // Build the query search params from the querySearchParams object e.g. "?a=123&b=456&c=abc"
        const query = viewIndex ? queryParamObjectToString({ viewIndex }) : '';
        return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_VIEW_ENTITIES_$entityId, { projectId, entityId })}${query}`;
    }


    /**
     * Go to the page that show details of an entity. By default, this will take the user to the VIEW_DATA entity details page
     * unless MANAGE_DATA usageMode is specified.
     */
    public gotoEntityDetailsPage (projectId: number, entityId: string, viewIndex?: number| undefined, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getEntityDetailsPageUrl(projectId, entityId, viewIndex), replace, delay);
    }


    /**
     * Get the alert rules listing page URL
     */
    public getAlertRulesPageUrl (projectId: number, queryParams?: {
        // TODO - Add filter attributes that we support
    }) {
        const query = queryParams ? queryParamObjectToString(queryParams) : '';
        return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ALERT_RULES, { projectId })}${query}`;
    }


    /**
     * Go to the page that list all the alert rules for the selected project.
     */
    public gotoAlertRulesPage (projectId: number, queryParams?: {
        // TODO - Add filter attributes that we support
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getAlertRulesPageUrl(projectId, queryParams), replace, delay);
    }


    /**
     * Get the alerts listing page URL
     */
    public getAlertsPageUrl (projectId: number, queryParams?: {
        // TODO - Add filter attributes that we support
    }) {
        const query = queryParams ? queryParamObjectToString(queryParams) : '';
        return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ALERTS, { projectId })}${query}`;
    }


    /**
     * Go to the page that list all the alerts for the selected project.
     */
    public gotoAlertsPage (projectId: number, queryParams?: {
        // TODO - Add filter attributes that we support
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getAlertsPageUrl(projectId, queryParams), replace, delay);
    }


    /**
     * Get the investigate alert page URL
     */
    public getInvestigateAlertPageUrl (projectId: number, alertId: string) {
        return fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_ALERTS_$alertId, { projectId, alertId });
    }


    /**
     * Go to the investigate alert page for the selected project.
     */
    public gotoInvestigateAlertPage (projectId: number, alertId: string, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getInvestigateAlertPageUrl(projectId, alertId), replace, delay);
    }


    /**
     * Get the Project's Home Historical Data page URL
     */
    public getHomeHistoricalDataPageUrl (params: {
        readonly projectId: number;
        readonly userId?: number | undefined;
        readonly homeId: number;
        readonly dataId: string;
        readonly tsdbSmartModuleId: string;
        readonly timeSeriesType: TimeSeriesType;
        readonly dataDisplayName?: string | undefined;
        readonly metricsDefinition?: {
            readonly entityClass: string;
            readonly metricsDefinitionId: string;
        } | undefined;
    }): string {

        // Set up the set of params we will add to the URL as Query Search params.
        const querySearchParams = {
            tsdbSmartModuleId: params.tsdbSmartModuleId,
            timeSeriesType   : params.timeSeriesType,
            dataDisplayName  : params.dataDisplayName,
            metricsDefinition: params.metricsDefinition ? JSON.stringify(params.metricsDefinition) : undefined,
        };

        // Build the query search params from the querySearchParams object e.g. "?a=123&b=456&c=abc"
        const query = queryParamObjectToString(querySearchParams);

        if (params.userId) {
            return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_TSDB_$dataId as string, params)}${query}`;
        }
        return `${fillInRouteParams(DEFAULT_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_TSDB_$dataId as string, params)}${query}`;
    }

    /**
     * Go to the Project Home Historical Data page
     */
    public gotoHomeHistoricalDataPage (params: {
        readonly projectId: number;
        readonly userId?: number | undefined;
        readonly homeId: number;
        readonly dataId: string;
        readonly tsdbSmartModuleId: string;
        readonly timeSeriesType: TimeSeriesType;
        readonly dataDisplayName?: string | undefined;
        readonly metricsDefinition?: {
            readonly entityClass: string;
            readonly metricsDefinitionId: string;
        } | undefined;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getHomeHistoricalDataPageUrl(params), replace, delay);
    }
}
